/** SQUARING WALLS WITH THE EDITOR'S OWN OPERATIONS — the one-off repair of the
 *  four 45° corner drafts (OB-42), and since OB-48 the editor's «Îndreaptă
 *  peretele» (one wall) and the module panel's «Îndreaptă-i» (every wall a few
 *  degrees off).
 *
 *  Imported before the importer squared diagonals, their walls sit 0.3°–2.3°
 *  off 45° (and P11 1.5° off 90°), with ends off the 5 cm grid. The repair runs
 *  the editor's own operations, so the result is what an architect would get by
 *  turning each wall by hand: `rotestePeretele` (openings and devices turn with
 *  the wall), `aseazaPeLiniaGrilei` for a diagonal, then every end that held a
 *  neighbour before and no longer does is stuck back with `mutaCapatulSingur`,
 *  thresholds that sat on a moved face follow it, and so do the rooms'
 *  corners (`roomsFollow`).
 *
 *  The repair runs once on prod rows through `zzSquare45.test.ts` and
 *  `architecture/tools/write_repaired_modules.py`; the editor keeps the rest. */
import type { Afin, Pt, TronsonDoc } from "../doc";
import {
  aseazaPeLiniaGrilei, axaPeretelui, conturulDupaDesen, mutaCapatulSingur, rotestePeretele,
} from "./model";
import { unghiulIndreptat, unghiulPatrat } from "./wallAngles";
/** Standard 1 of the architects' prompts: no door or window without 10 cm of
 *  wall beside it. */
const EAR_M = 0.1;

export type RepairReport = {
  moved: { id: string; from: number; to: number }[];
  left: { id: string; angle: number }[];
  rooms: { before: { kind: string; area: number }[]; after: { kind: string; area: number }[] };
  shortEars: { kind: "door" | "window"; wall: string; ear: number }[];
};

const angleOf = (a: Pt, b: Pt) => (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI;

/** The angle a wall at `u` (degrees) is squared to, or `null`. */
export const squaredAngle = unghiulPatrat;

const val = (v: Afin | number) => (typeof v === "number" ? v : v.a);

function roomAreas(doc: TronsonDoc) {
  return (doc.plan?.rooms ?? []).map((r) => {
    const p = r.poly.map((v) => [val(v[0]), val(v[1])] as Pt);
    const a = Math.abs(p.reduce((s, q, i) => {
      const o = p[(i + 1) % p.length];
      return s + q[0] * o[1] - o[0] * q[1];
    }, 0)) / 2;
    return { kind: r.kind, area: Math.round(a * 100) / 100 };
  });
}

/** Where the line through `p` along `d` crosses the line through `q` along `e`. */
function cross(p: Pt, d: Pt, q: Pt, e: Pt): Pt | null {
  const den = d[0] * e[1] - d[1] * e[0];
  if (Math.abs(den) < 1e-12) return null;
  const s = ((q[0] - p[0]) * e[1] - (q[1] - p[1]) * e[0]) / den;
  return [p[0] + d[0] * s, p[1] + d[1] * s];
}

type Face = { p: Pt; u: Pt; L: number };
function faces(doc: TronsonDoc, i: number): [Face, Face] {
  const w = doc.plan!.walls![i];
  const [a, b] = axaPeretelui(w);
  const L = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
  const u: Pt = [(b[0] - a[0]) / L, (b[1] - a[1]) / L];
  const h = (w.thickness_m ?? 0) / 2;
  const f = (s: number): Face => ({ p: [a[0] - u[1] * h * s, a[1] + u[0] * h * s], u, L });
  return [f(1), f(-1)];
}

const onFace = (f: Face, p: Pt, tol: number) => {
  const t = (p[0] - f.p[0]) * f.u[0] + (p[1] - f.p[1]) * f.u[1];
  const d = Math.abs((p[0] - f.p[0]) * f.u[1] - (p[1] - f.p[1]) * f.u[0]);
  return d <= tol && t >= -tol && t <= f.L + tol;
};

/** Does the end `c` of wall `j` touch another wall — within two millimetres of
 *  its rectangle? A butt end resting on a face touches; `capetePrinse` asks
 *  for the body, which an end stopped exactly on the face does not reach. */
export function touches(doc: TronsonDoc, j: number, c: 0 | 1): boolean {
  const walls = doc.plan?.walls ?? [];
  const p = axaPeretelui(walls[j])[c];
  return walls.some((w, i) => {
    if (i === j || w.bulge) return false;
    const [a, b] = axaPeretelui(w);
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (L < 1e-9) return false;
    const t = ((p[0] - a[0]) * (b[0] - a[0]) + (p[1] - a[1]) * (b[1] - a[1])) / L;
    const n = Math.abs((p[0] - a[0]) * (b[1] - a[1]) - (p[1] - a[1]) * (b[0] - a[0])) / L;
    const out = Math.hypot(Math.max(0, -t, t - L), Math.max(0, n - (w.thickness_m ?? 0) / 2));
    return out <= 2e-3;
  });
}

export function squareWalls(
  doc0: TronsonDoc,
  /** Only these walls (by id); all by default. */
  only?: ReadonlySet<string>,
  /** Where a wall at `u` goes, or `null` to leave it; the importer's windows by default. */
  target: (u: number) => number | null = squaredAngle,
  /** The second pass, over walls the first one bent (below). */
  again = false,
): { doc: TronsonDoc; report: RepairReport } {
  const walls0 = doc0.plan?.walls ?? [];
  const held0 = walls0.map((_, i) => [touches(doc0, i, 0), touches(doc0, i, 1)]);
  const ends0 = walls0.map((w) => axaPeretelui(w));
  const report: RepairReport = {
    moved: [], left: [], rooms: { before: roomAreas(doc0), after: [] }, shortEars: [],
  };

  let doc = doc0;
  const moved = new Set<number>();
  walls0.forEach((w, i) => {
    if (w.bulge || (only && !only.has(w.id))) return;
    const [a, b] = ends0[i];
    const u = angleOf(a, b);
    const to = target(u);
    if (to === null || Math.abs(to - u) < 1e-9) return;
    doc = aseazaPeLiniaGrilei(rotestePeretele(doc, i, to - u), i);
    moved.add(i);
    report.moved.push({ id: w.id, from: Math.round(u * 100) / 100, to });
  });

  // Every end that touched a wall before and no longer does goes back to it:
  // a turned wall's own end toward where it was, a neighbour's end a millimetre
  // outward, so `mutaCapatulSingur` finds the moved face and stops on it along
  // the end's own line.
  walls0.forEach((_, j) => {
    for (const c of [0, 1] as const) {
      if (!held0[j][c] || touches(doc, j, c)) continue;
      const [a, b] = axaPeretelui(doc.plan!.walls![j]);
      const end = c ? b : a;
      const other = c ? a : b;
      let delta: Pt = [ends0[j][c][0] - end[0], ends0[j][c][1] - end[1]];
      if (Math.hypot(delta[0], delta[1]) < 1e-6) {
        const L = Math.hypot(end[0] - other[0], end[1] - other[1]) || 1;
        delta = [(end[0] - other[0]) / L * 0.001, (end[1] - other[1]) / L * 0.001];
      }
      doc = mutaCapatulSingur(doc, j, c, delta, true);
    }
  });

  // Thresholds that sat on a moved wall's face follow that face, along their
  // own line, so the rooms they divide stay divided.
  const ths = doc.plan?.thresholds;
  if (ths?.length && moved.size) {
    const out = JSON.parse(JSON.stringify(doc)) as TronsonDoc;
    for (const t of out.plan!.thresholds!) {
      const p0: Pt = [val(t[0][0]), val(t[0][1])];
      const p1: Pt = [val(t[1][0]), val(t[1][1])];
      const L = Math.hypot(p1[0] - p0[0], p1[1] - p0[1]) || 1;
      const dir: Pt = [(p1[0] - p0[0]) / L, (p1[1] - p0[1]) / L];
      for (const [k, p] of [[0, p0], [1, p1]] as const) {
        for (const i of moved) {
          const before = faces(doc0, i);
          const s = before.findIndex((f) => onFace(f, p, 0.01));
          if (s < 0) continue;
          const f = faces(doc, i)[s];
          const q = cross(p, dir, f.p, f.u);
          if (q) {
            (t[k][0] as Afin).a = Math.round(q[0] * 1e4) / 1e4;
            (t[k][1] as Afin).a = Math.round(q[1] * 1e4) / 1e4;
          }
          break;
        }
      }
    }
    doc = out;
  }

  doc = roomsFollow(doc0, conturulDupaDesen(doc0, doc));

  // A SQUARE WALL STAYS SQUARE. Sticking an end back takes the nearest point of
  // the moved face or corner, which bends a neighbour that was exact: L1g on
  // PREMIUM COLT 45, resting on the end of the turned P10, came back at
  // 134.92° from 135°, and the panel still counted it off. One more pass
  // squares the walls bent so.
  if (!again) {
    const bent = new Set(walls0.filter((w, i) => {
      if (w.bulge || moved.has(i)) return false;
      const u0 = angleOf(...ends0[i]);
      const u1 = angleOf(...axaPeretelui(doc.plan!.walls![i]));
      return squaredAngle(u0) !== null && Math.abs(squaredAngle(u0)! - u0) < 1e-9
        && Math.abs(u1 - u0) > 1e-6;
    }).map((w) => w.id));
    if (bent.size) {
      const r = squareWalls(doc, bent, squaredAngle, true);
      doc = r.doc;
      report.moved.push(...r.report.moved);
    }
  }
  report.rooms.after = roomAreas(doc);

  (doc.plan?.walls ?? []).forEach((w) => {
    const [a, b] = axaPeretelui(w);
    const u = angleOf(a, b);
    const r = ((u % 45) + 45) % 45;
    if (!w.bulge && Math.min(r, 45 - r) > 0.05) report.left.push({ id: w.id, angle: Math.round(u * 100) / 100 });
  });

  for (const kind of ["door", "window"] as const) {
    for (const o of (kind === "door" ? doc.plan?.doors : doc.plan?.windows) ?? []) {
      const host = (o as { wall?: string }).wall;
      const w = doc.plan!.walls!.find((x) => x.id === host);
      if (!w || !moved.has(doc.plan!.walls!.indexOf(w))) continue;
      const [a, b] = axaPeretelui(w);
      const L = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
      const t = ((val(o.x) - a[0]) * (b[0] - a[0]) + (val(o.y) - a[1]) * (b[1] - a[1])) / L;
      const half = (o.w_m ?? 0) / 2;
      const ear = Math.min(t - half, L - t - half);
      if (ear < EAR_M) report.shortEars.push({ kind, wall: w.id, ear: Math.round(ear * 1000) / 1000 });
    }
  }
  return { doc, report };
}

/** THE ROOMS FOLLOW THE FACES THEY SIT ON; they are not rebuilt.
 *
 *  A rebuild re-derives every room from the walls, and the hand-drawn corner
 *  drafts do not match the space between their walls: STANDARD COLT 45 draws
 *  its stair hall over the lift and two shafts (the rebuild cut it from 60.35
 *  to 50.90 m²), PREMIUM COLT 45 has rooms under the rebuild's 0.6 m² sliver
 *  size (two vanished) and a hall the walls leave open to the facade notch
 *  (P11 stops 52 cm short of the stair block; the hall took 148 m²). Squaring
 *  moves faces by centimetres, so each room corner lying on a turned wall's
 *  face moves onto that face's new line, along the room's own side that leaves
 *  the face (as thresholds do above), or to where two turned faces now cross.
 *  Nothing is added, dropped or reordered, and a room with no such corner is
 *  written back as it was.
 *
 *  A declared area moves by what the polygon gained or lost, not to the
 *  polygon's area: PREMIUM's hall is declared 34.48 over a 33.57 m² outline. */
function roomsFollow(doc0: TronsonDoc, doc: TronsonDoc): TronsonDoc {
  const W0 = doc0.plan?.walls ?? [];
  const W1 = doc.plan?.walls ?? [];
  const sameLine = (f: Face, g: Face) =>
    Math.abs(f.u[0] * g.u[1] - f.u[1] * g.u[0]) < 1e-9
    && Math.abs((g.p[0] - f.p[0]) * f.u[1] - (g.p[1] - f.p[1]) * f.u[0]) < 1e-6;
  const turned = W0.map((_, i) => i).filter((i) => W1[i] && !W0[i].bulge
    && !sameLine(faces(doc0, i)[0], faces(doc, i)[0]));
  if (!turned.length || !doc0.plan?.rooms?.length) return doc;
  // No corner moves further than a turned wall's end did, plus the slack of a
  // corner drawn a few millimetres off the face.
  let reach = 0;
  for (const i of turned) {
    const a = axaPeretelui(W0[i]), b = axaPeretelui(W1[i]);
    for (const c of [0, 1]) reach = Math.max(reach, Math.hypot(a[c][0] - b[c][0], a[c][1] - b[c][1]));
  }
  reach += 0.02;
  const line = (f: Face, p: Pt): Pt => {
    const t = (p[0] - f.p[0]) * f.u[0] + (p[1] - f.p[1]) * f.u[1];
    return [f.p[0] + f.u[0] * t, f.p[1] + f.u[1] * t];
  };
  const out = JSON.parse(JSON.stringify(doc)) as TronsonDoc;
  out.plan!.rooms = doc0.plan.rooms.map((r) => {
    const P = r.poly.map((v) => [val(v[0]), val(v[1])] as Pt);
    let moved = false;
    const Q = P.map((p, k): Pt => {
      const hits: { i: number; s: number }[] = [];
      for (const i of turned) {
        faces(doc0, i).forEach((f, s) => { if (onFace(f, p, 0.01)) hits.push({ i, s }); });
      }
      if (!hits.length) return p;
      const f0 = faces(doc0, hits[0].i)[hits[0].s];
      const f1 = faces(doc, hits[0].i)[hits[0].s];
      const other = hits.find((h) => h.i !== hits[0].i);
      let q: Pt | null = null;
      if (other) {
        const g1 = faces(doc, other.i)[other.s];
        q = cross(f1.p, f1.u, g1.p, g1.u);
      } else {
        // The side that leaves the face most squarely: its direction is the
        // steadiest to slide along.
        let best = 0;
        for (const n of [P[(k + P.length - 1) % P.length], P[(k + 1) % P.length]]) {
          const L = Math.hypot(n[0] - p[0], n[1] - p[1]);
          if (L < 1e-9) continue;
          const d: Pt = [(n[0] - p[0]) / L, (n[1] - p[1]) / L];
          const sin = Math.abs(d[0] * f0.u[1] - d[1] * f0.u[0]);
          if (sin > best + 1e-9) { best = sin; q = cross(p, d, f1.p, f1.u); }
        }
        if (best < 0.1) q = null;
      }
      if (!q || Math.hypot(q[0] - p[0], q[1] - p[1]) > reach) q = line(f1, p);
      if (Math.hypot(q[0] - p[0], q[1] - p[1]) < 1e-6) return p;
      moved = true;
      return [Math.round(q[0] * 1e4) / 1e4, Math.round(q[1] * 1e4) / 1e4];
    });
    if (!moved) return r;
    const area = (ps: Pt[]) => Math.abs(ps.reduce((s, q, i) => {
      const o = ps[(i + 1) % ps.length];
      return s + q[0] * o[1] - o[0] * q[1];
    }, 0)) / 2;
    return {
      ...r,
      poly: r.poly.map((v, k) => [{ ...v[0], a: Q[k][0] }, { ...v[1], a: Q[k][1] }]),
      area_m2: Math.round(((r.area_m2 ?? area(P)) + area(Q) - area(P)) * 100) / 100,
    } as typeof r;
  });
  return out;
}

/** «Îndreaptă peretele»: wall `i` turned to the nearest multiple of 45°, its
 *  held ends kept on their neighbours, a diagonal on its grid line. */
export function indreaptaPeretele45(
  doc: TronsonDoc, i: number,
): { doc: TronsonDoc; report: RepairReport } {
  const w = doc.plan?.walls?.[i];
  if (!w) return squareWalls(doc, new Set());
  return squareWalls(doc, new Set([w.id]), unghiulIndreptat);
}
